import type { PostgrestClient } from "@supabase/postgrest-js";
import type { DatabaseWithOptions } from "../../../__definitions__/database-definitions";
import { usePermissions } from "../../../server/auth/use-permissions";
import { useSupabaseLoader } from "../../../server/supabase/hooks";
import type { UseSupabaseLoaderState } from "../../../server/supabase/hooks/types";
import type { UUID } from "../../../utils/uuid";
import { SupabaseCustomEmailService } from "../../channels/email/server/supabase-custom-email-service";
import type { ChannelName } from "../../channels/types";
import { SupabaseEmailDesignService } from "../../email-design/server/supabase-email-design-service";
import { EmailMomentLoader } from "../../email-moment/server/email-moment-loader";
import type { EmailMoment } from "../../email-moment/types";
import { SupabaseEventService } from "../../event/server/supabase-event-service";
import { SupabaseAccountService } from "../../generic/server/supabase-account-service";
import { SupabaseMergeTagService } from "../../merge-tag/server/supabase-merge-tag-service";
import type { Tags } from "../../merge-tag/types";
import {
  getMomentRestrictions,
  type MomentRestrictions,
} from "../../moment/hooks/use-moment-restrictions";
import type { GetPersonsInSegmentJsonPerson } from "../../segment/components/manager-v2/types";
import { SupabaseSlackAppService } from "../../slack/server/slack-supabase-service";
import type { FollowUpDetailsWithConfig, FollowUpMomentDetails } from "../server/types";
import { isFollowUpDetailsWithConfig } from "../utils";

function extractUseLexicalEditor(momentDetails: FollowUpMomentDetails): boolean {
  return (
    ((momentDetails as Record<string, unknown>).use_lexical_editor as boolean | undefined) ?? true
  );
}

export type FollowUpMomentDetailsConfig =
  | { type: "follow_up"; data: UUID }
  | { type: "from_moment"; data: { moment_id: UUID } }
  | { type: "from_survey"; data: { moment_id: UUID; survey_id: UUID } };

export type FollowUpSegmentPerson = Omit<GetPersonsInSegmentJsonPerson, "channels"> & {
  channels: Partial<Record<NonNullable<ChannelName>, string>>;
};

export type FollowUpMomentDetailsLoaderData = {
  momentFollowUpDetails: FollowUpMomentDetails;
  enabledChannels: NonNullable<ChannelName>[];
  segmentPersons?: FollowUpSegmentPerson[];
  mergeTags: Tags;
  slackVideoEnabled: boolean;
  restrictions: MomentRestrictions;
  customEmailAddresses: { id: UUID; address: string }[];
  followUp?: FollowUpDetailsWithConfig;
  useLexicalEditor: boolean;
  dynamicSegmentIdMap?: Record<string, string> | null;
  sourceEmailMomentSubject?: string | null;
  sourceEmailMoment?: EmailMoment | null;
};

export function useFollowUpMomentDetails(
  config: FollowUpMomentDetailsConfig,
): UseSupabaseLoaderState<FollowUpMomentDetailsLoaderData> {
  const { ce_admin } = usePermissions();

  return useSupabaseLoader(
    async ({ supabase, account_id, abortController }) => {
      const [
        {
          momentFollowUpDetails,
          followUp,
          dynamicSegmentIdMap,
          sourceEmailMomentSubject,
          sourceEmailMoment,
        },
        enabledChannels,
        mergeTags,
        slackVideoEnabled,
        restrictions,
        customEmailAddresses,
        defaultEmailDesign,
      ] = await Promise.all([
        loadMomentAndFollowUp(supabase, account_id, config, abortController),
        new SupabaseAccountService(supabase).getEnabledChannels(account_id),
        new SupabaseMergeTagService(supabase).getAllMergeTags(),
        new SupabaseSlackAppService(supabase).videoEnabled(account_id),
        getMomentRestrictions(ce_admin, supabase),
        new SupabaseCustomEmailService(supabase).getAllVerified(account_id),
        new SupabaseEmailDesignService(supabase).getDefaultDesignId(),
      ]);

      const useLexicalEditor = extractUseLexicalEditor(momentFollowUpDetails);

      if (momentFollowUpDetails.segment?.segment_json) {
        const { data: persons, error: err } = await supabase.rpc("get_persons_in_segment_json", {
          account_id_: account_id,
          segment_json: momentFollowUpDetails.segment.segment_json,
          target_manager_: momentFollowUpDetails.target_manager ?? undefined,
          target_connection_type_: momentFollowUpDetails.target_connection_type ?? undefined,
        });

        if (err) {
          console.error("Error loading persons for Follow-Up segment:", err);
          throw err;
        }

        return {
          momentFollowUpDetails: {
            ...momentFollowUpDetails,
            email_design_id:
              momentFollowUpDetails.email_design_id ?? defaultEmailDesign?.data?.id ?? null,
          },
          enabledChannels,
          segmentPersons: persons.map((p) => ({
            ...p,
            channels: p.channels as Partial<Record<NonNullable<ChannelName>, string>>,
          })),
          mergeTags,
          slackVideoEnabled,
          followUp,
          restrictions,
          customEmailAddresses: customEmailAddresses.data ?? [],
          useLexicalEditor,
          dynamicSegmentIdMap: dynamicSegmentIdMap ?? null,
          sourceEmailMomentSubject,
          sourceEmailMoment,
        };
      }

      return {
        momentFollowUpDetails: {
          ...momentFollowUpDetails,
          email_design_id:
            momentFollowUpDetails.email_design_id ?? defaultEmailDesign?.data?.id ?? null,
        },
        enabledChannels,
        mergeTags,
        slackVideoEnabled,
        followUp,
        restrictions,
        customEmailAddresses: customEmailAddresses.data ?? [],
        useLexicalEditor,
        dynamicSegmentIdMap: dynamicSegmentIdMap ?? null,
        sourceEmailMomentSubject,
        sourceEmailMoment,
      };
    },
    [config, ce_admin],
  );
}

async function loadMomentAndFollowUp(
  supabase: PostgrestClient<DatabaseWithOptions["db"], DatabaseWithOptions["options"], "public">,
  account_id: UUID,
  config: FollowUpMomentDetailsConfig,
  abortController: AbortController | null,
): Promise<
  Pick<
    FollowUpMomentDetailsLoaderData,
    | "momentFollowUpDetails"
    | "followUp"
    | "dynamicSegmentIdMap"
    | "sourceEmailMomentSubject"
    | "sourceEmailMoment"
  >
> {
  switch (config.type) {
    case "from_moment":
    case "from_survey":
      return loadFromMomentId(supabase, config.data.moment_id, account_id);
    case "follow_up":
      return loadFromFollowUpId(supabase, account_id, config.data, abortController);
  }
}

async function loadFromFollowUpId(
  supabase: PostgrestClient<DatabaseWithOptions["db"], DatabaseWithOptions["options"], "public">,
  account_id: UUID,
  follow_up_id: UUID,
  abortController: AbortController | null,
): Promise<
  Pick<
    FollowUpMomentDetailsLoaderData,
    "momentFollowUpDetails" | "followUp" | "dynamicSegmentIdMap" | "sourceEmailMomentSubject"
  >
> {
  const { data: followUp, error: followUpError } = await supabase
    .rpc("follow_up_details", {
      follow_up_id_: follow_up_id,
      account_id_: account_id,
    })
    .single();

  if (followUpError) {
    if (!abortController?.signal?.aborted) console.error("Error loading Follow-Up:", followUpError);
    throw followUpError;
  }

  if (!isFollowUpDetailsWithConfig(followUp)) {
    throw new Error("Follow-Up does not have a linked Communication");
  }

  if (followUp.schedule?.event_key) {
    const { data: events, error: eventError } = await new SupabaseEventService(supabase).getKeys({
      mustIncludeKey: followUp.schedule.event_key,
      includeLibrary: false,
    });
    if (eventError) throw eventError;
    followUp.schedule_event = events?.find((e) => e.key === followUp.schedule?.event_key) ?? null;
  }

  const { data: momentFollowUpDetails, error: momentFollowUpDetailsError } = await supabase
    .rpc("follow_up_moment_details", {
      moment_id_: followUp.config.source_id as UUID,
      account_id_: account_id,
    })
    .single();

  if (momentFollowUpDetailsError) throw momentFollowUpDetailsError;

  let emailMoment = null;
  let sourceEmailMoment = null;
  let dynamicSegmentIdMap: Record<string, string> | null = null;
  const useLexicalEditor = extractUseLexicalEditor(momentFollowUpDetails);

  const [followUpMomentResult, sourceMomentResult] = await Promise.all([
    !useLexicalEditor && followUp.moment_id
      ? supabase
          .from("moment")
          .select("email_moment_id, dynamic_segment_id_map")
          .eq("id", followUp.moment_id)
          .single()
      : Promise.resolve({ data: null }),
    supabase
      .from("moment")
      .select("email_moment_id")
      .eq("id", followUp.config.source_id as UUID)
      .single(),
  ]);

  if (!useLexicalEditor) {
    const momentRecord = followUpMomentResult.data;
    if (momentRecord?.email_moment_id) {
      const { data: loadedEmailMoment } = await new EmailMomentLoader(supabase).load(
        momentRecord.email_moment_id,
      );
      emailMoment = loadedEmailMoment ?? null;
    }
    dynamicSegmentIdMap = (momentRecord?.dynamic_segment_id_map as Record<string, string>) ?? null;
  }

  const sourceMomentRecord = sourceMomentResult.data;
  if (sourceMomentRecord?.email_moment_id) {
    const { data: loadedSourceEmailMoment } = await new EmailMomentLoader(supabase).load(
      sourceMomentRecord.email_moment_id,
    );
    sourceEmailMoment = loadedSourceEmailMoment ?? null;
  }

  return {
    momentFollowUpDetails,
    followUp: {
      ...followUp,
      emailMoment,
      sourceEmailMoment,
    },
    dynamicSegmentIdMap,
    sourceEmailMomentSubject: emailMoment?.subject ?? null,
  };
}

async function loadFromMomentId(
  supabase: PostgrestClient<DatabaseWithOptions["db"], DatabaseWithOptions["options"], "public">,
  moment_id: UUID,
  account_id: UUID,
): Promise<
  Pick<
    FollowUpMomentDetailsLoaderData,
    | "momentFollowUpDetails"
    | "dynamicSegmentIdMap"
    | "sourceEmailMomentSubject"
    | "sourceEmailMoment"
  >
> {
  const [{ data: momentFollowUpDetails, error: momentFollowUpDetailsError }] = await Promise.all([
    supabase
      .rpc("follow_up_moment_details", {
        moment_id_: moment_id,
        account_id_: account_id,
      })
      .single(),
  ]);

  if (momentFollowUpDetailsError) throw momentFollowUpDetailsError;

  const useLexicalEditor = extractUseLexicalEditor(momentFollowUpDetails);
  let sourceEmailMomentSubject: string | null = null;
  let sourceEmailMoment: EmailMoment | null = null;

  // Load email moment for Unlayer newsletters: explicit use_lexical_editor=false, or old newsletters
  // where use_lexical_editor is null (defaults to true) but there is no Lexical design.
  if (!useLexicalEditor || !momentFollowUpDetails.design) {
    const { data: momentRecord } = await supabase
      .from("moment")
      .select("email_moment_id")
      .eq("id", moment_id)
      .single();
    if (momentRecord?.email_moment_id) {
      const { data: loadedEmailMoment } = await new EmailMomentLoader(supabase).load(
        momentRecord.email_moment_id,
      );
      sourceEmailMomentSubject = loadedEmailMoment?.subject ?? null;
      sourceEmailMoment = loadedEmailMoment ?? null;
    }
  }

  return {
    momentFollowUpDetails,
    dynamicSegmentIdMap: null,
    sourceEmailMomentSubject,
    sourceEmailMoment,
  };
}
